import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { Crown, Gift, UserX, Search, Shield, CreditCard, History } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_OPTIONS = [
  { value: 'free', label: 'Free', color: 'bg-gray-100 text-gray-700' },
  { value: 'starter', label: 'Starter', color: 'bg-blue-100 text-blue-700' },
  { value: 'pro', label: 'Pro', color: 'bg-amber-100 text-amber-700' },
  { value: 'ministry', label: 'Ministry', color: 'bg-purple-100 text-purple-700' },
  { value: 'enterprise', label: 'Enterprise', color: 'bg-green-100 text-green-700' },
];

export default function AdminSubscriptionManager({ token }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [subscriptions, setSubscriptions] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showOverrideDialog, setShowOverrideDialog] = useState(false);
  const [showCreditsDialog, setShowCreditsDialog] = useState(false);
  const [showRevokeDialog, setShowRevokeDialog] = useState(false);
  
  // Override form state
  const [overrideForm, setOverrideForm] = useState({
    tier: 'pro',
    reason: '',
    isLifetime: false,
    waiveFees: true,
    bonusCredits: 0,
  });
  
  // Credits form state
  const [creditsForm, setCreditsForm] = useState({
    credits: 100,
    reason: '',
  });
  
  // Revoke form state
  const [revokeReason, setRevokeReason] = useState('');

  const fetchOverrides = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/subscriptions/overrides`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setOverrides(data.overrides || []);
      }
    } catch (error) {
      console.error('Failed to fetch overrides:', error);
    }
  };

  useEffect(() => {
    if (token) {
      fetchOverrides();
    }
  }, [token]);

  const searchUsers = async () => {
    if (!searchQuery.trim()) return;
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/users?search=${encodeURIComponent(searchQuery)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        // Get subscription info for each user
        const usersWithSubs = await Promise.all(
          (data.users || []).map(async (user) => {
            try {
              const subRes = await fetch(`${API_URL}/api/admin/subscriptions/${user.user_id}`, {
                headers: { Authorization: `Bearer ${token}` },
              });
              if (subRes.ok) {
                const subData = await subRes.json();
                return { ...user, subscription: subData.subscription, usage: subData.usage };
              }
            } catch (e) {
              console.error('Failed to fetch user subscription:', e);
            }
            return { ...user, subscription: { tier: 'free' } };
          })
        );
        setSubscriptions(usersWithSubs);
      }
    } catch (error) {
      toast.error('Failed to search users');
    } finally {
      setLoading(false);
    }
  };

  const handleOverride = async () => {
    if (!selectedUser || !overrideForm.reason) {
      toast.error('Please provide a reason for the override');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/admin/subscriptions/override`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          user_id: selectedUser.user_id,
          tier: overrideForm.tier,
          reason: overrideForm.reason,
          is_lifetime: overrideForm.isLifetime,
          waive_fees: overrideForm.waiveFees,
          bonus_ai_credits: overrideForm.bonusCredits,
        }),
      });
      
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        setShowOverrideDialog(false);
        fetchOverrides();
        searchUsers();
        resetForms();
      } else {
        toast.error(data.detail || 'Failed to override subscription');
      }
    } catch (error) {
      toast.error('Failed to override subscription');
    }
  };

  const handleGrantCredits = async () => {
    if (!selectedUser || !creditsForm.reason) {
      toast.error('Please provide a reason for granting credits');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/admin/subscriptions/grant-credits`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          user_id: selectedUser.user_id,
          credits: creditsForm.credits,
          reason: creditsForm.reason,
        }),
      });
      
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        setShowCreditsDialog(false);
        searchUsers();
        resetForms();
      } else {
        toast.error(data.detail || 'Failed to grant credits');
      }
    } catch (error) {
      toast.error('Failed to grant credits');
    }
  };

  const handleRevoke = async () => {
    if (!selectedUser || !revokeReason) {
      toast.error('Please provide a reason for revoking');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/admin/subscriptions/revoke-override`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          user_id: selectedUser.user_id,
          reason: revokeReason,
        }),
      });
      
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        setShowRevokeDialog(false);
        fetchOverrides();
        searchUsers();
        resetForms();
      } else {
        toast.error(data.detail || 'Failed to revoke override');
      }
    } catch (error) {
      toast.error('Failed to revoke override');
    }
  };

  const resetForms = () => {
    setOverrideForm({ tier: 'pro', reason: '', isLifetime: false, waiveFees: true, bonusCredits: 0 });
    setCreditsForm({ credits: 100, reason: '' });
    setRevokeReason('');
    setSelectedUser(null);
  };

  const getTierBadge = (tier) => {
    const tierConfig = TIER_OPTIONS.find(t => t.value === tier) || TIER_OPTIONS[0];
    return <Badge className={tierConfig.color}>{tierConfig.label}</Badge>;
  };

  return (
    <div className="space-y-6">
      {/* Search Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Subscription Management
          </CardTitle>
          <CardDescription>
            Override plans, grant credits, and manage user subscriptions
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Input
              placeholder="Search by email or name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && searchUsers()}
              className="flex-1"
            />
            <Button onClick={searchUsers} disabled={loading}>
              <Search className="w-4 h-4 mr-2" />
              {loading ? 'Searching...' : 'Search'}
            </Button>
          </div>
          
          {/* Search Results */}
          {subscriptions.length > 0 && (
            <div className="mt-4 space-y-2">
              {subscriptions.map((user) => (
                <div
                  key={user.user_id}
                  className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div>
                      <p className="font-medium">{user.name || 'Unknown'}</p>
                      <p className="text-sm text-gray-500">{user.email}</p>
                    </div>
                    {getTierBadge(user.subscription?.tier || 'free')}
                    {user.subscription?.admin_override && (
                      <Badge variant="outline" className="border-amber-500 text-amber-600">
                        <Crown className="w-3 h-3 mr-1" /> Admin Override
                      </Badge>
                    )}
                    {user.subscription?.is_lifetime && (
                      <Badge className="bg-green-500">Lifetime</Badge>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setSelectedUser(user);
                        setShowCreditsDialog(true);
                      }}
                    >
                      <Gift className="w-4 h-4 mr-1" /> Credits
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => {
                        setSelectedUser(user);
                        setShowOverrideDialog(true);
                      }}
                    >
                      <Crown className="w-4 h-4 mr-1" /> Override
                    </Button>
                    {user.subscription?.admin_override && (
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => {
                          setSelectedUser(user);
                          setShowRevokeDialog(true);
                        }}
                      >
                        <UserX className="w-4 h-4 mr-1" /> Revoke
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Active Overrides */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Crown className="w-5 h-5 text-amber-500" />
            Active Admin Overrides
          </CardTitle>
          <CardDescription>
            Users with admin-granted subscriptions
          </CardDescription>
        </CardHeader>
        <CardContent>
          {overrides.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No active overrides</p>
          ) : (
            <div className="space-y-2">
              {overrides.map((override) => (
                <div
                  key={override.user_id}
                  className="flex items-center justify-between p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800"
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{override.user?.name || 'Unknown'}</p>
                      {getTierBadge(override.tier)}
                      {override.is_lifetime && <Badge className="bg-green-500">Lifetime</Badge>}
                      {override.waive_fees && <Badge variant="outline">Fees Waived</Badge>}
                    </div>
                    <p className="text-sm text-gray-500">{override.user?.email}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      Reason: {override.admin_override_reason}
                    </p>
                    {override.granted_by && (
                      <p className="text-xs text-gray-400">
                        Granted by: {override.granted_by.name || override.granted_by.email}
                      </p>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => {
                      setSelectedUser({ user_id: override.user_id, ...override.user });
                      setShowRevokeDialog(true);
                    }}
                  >
                    <UserX className="w-4 h-4 mr-1" /> Revoke
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Override Dialog */}
      <Dialog open={showOverrideDialog} onOpenChange={setShowOverrideDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Override Subscription</DialogTitle>
            <DialogDescription>
              Grant {selectedUser?.name || selectedUser?.email} a subscription plan
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Plan</Label>
              <Select value={overrideForm.tier} onValueChange={(v) => setOverrideForm({ ...overrideForm, tier: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIER_OPTIONS.map((tier) => (
                    <SelectItem key={tier.value} value={tier.value}>
                      {tier.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Reason *</Label>
              <Textarea
                placeholder="Why are you granting this plan?"
                value={overrideForm.reason}
                onChange={(e) => setOverrideForm({ ...overrideForm, reason: e.target.value })}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <Label>Lifetime Access</Label>
              <Switch
                checked={overrideForm.isLifetime}
                onCheckedChange={(v) => setOverrideForm({ ...overrideForm, isLifetime: v })}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <Label>Waive All Fees</Label>
              <Switch
                checked={overrideForm.waiveFees}
                onCheckedChange={(v) => setOverrideForm({ ...overrideForm, waiveFees: v })}
              />
            </div>
            
            <div>
              <Label>Bonus AI Credits</Label>
              <Input
                type="number"
                min="0"
                value={overrideForm.bonusCredits}
                onChange={(e) => setOverrideForm({ ...overrideForm, bonusCredits: parseInt(e.target.value) || 0 })}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOverrideDialog(false)}>Cancel</Button>
            <Button onClick={handleOverride}>Grant Plan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Grant Credits Dialog */}
      <Dialog open={showCreditsDialog} onOpenChange={setShowCreditsDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Grant AI Credits</DialogTitle>
            <DialogDescription>
              Give bonus AI credits to {selectedUser?.name || selectedUser?.email}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Number of Credits</Label>
              <Input
                type="number"
                min="1"
                value={creditsForm.credits}
                onChange={(e) => setCreditsForm({ ...creditsForm, credits: parseInt(e.target.value) || 0 })}
              />
            </div>
            
            <div>
              <Label>Reason *</Label>
              <Textarea
                placeholder="Why are you granting these credits?"
                value={creditsForm.reason}
                onChange={(e) => setCreditsForm({ ...creditsForm, reason: e.target.value })}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreditsDialog(false)}>Cancel</Button>
            <Button onClick={handleGrantCredits}>
              <Gift className="w-4 h-4 mr-2" /> Grant {creditsForm.credits} Credits
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revoke Dialog */}
      <Dialog open={showRevokeDialog} onOpenChange={setShowRevokeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revoke Override</DialogTitle>
            <DialogDescription>
              Remove admin override from {selectedUser?.name || selectedUser?.email}. They will be returned to the free tier.
            </DialogDescription>
          </DialogHeader>
          
          <div>
            <Label>Reason *</Label>
            <Textarea
              placeholder="Why are you revoking this override?"
              value={revokeReason}
              onChange={(e) => setRevokeReason(e.target.value)}
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRevokeDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleRevoke}>
              <UserX className="w-4 h-4 mr-2" /> Revoke Override
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
